import axios from 'axios';
import type { Ref } from 'vue';
import { ref, watch } from 'vue';

import { CapacityProviderType } from '@rygen/rygen-ui/model';

import api from '@/api';
import { CarrierSearchFilter } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

// The answer belongs to the client, not to the component asking, and an order
// renders one tab set per shipment spec, which all mount together. Sharing the
// in-flight request keeps that a single call instead of one per tab. It is
// dropped once it settles, so a later mount sees newly configured brokers.
const requestsByClientId = new Map<number, Promise<boolean>>();

const fetchHasConfiguredBrokers = (clientId: number): Promise<boolean> => {
  const existing = requestsByClientId.get(clientId);
  if (existing) {
    return existing;
  }

  const request = api.carrier
    .search(
      new CarrierSearchFilter({
        pageSize: 1,
        type: CapacityProviderType.BROKER,
      }),
    )
    .then((result) => result.totalElements > 0)
    .catch((e) => {
      if (!axios.isCancel(e)) {
        console.error('Error checking for configured brokers', e);
      }

      return false;
    })
    .finally(() => requestsByClientId.delete(clientId));

  requestsByClientId.set(clientId, request);
  return request;
};

/**
 * Whether the owning client has any broker configured, used to gate the
 * brokerage actions. Only the total matters, so it asks for a single row.
 */
export const useHasConfiguredBrokers = (): Ref<boolean> => {
  const { owningClient } = useParentEntityStore();
  const hasConfiguredBrokers = ref(false);

  watch(
    () => owningClient.value?.id,
    async (clientId?: number) => {
      if (!clientId) {
        hasConfiguredBrokers.value = false;
        return;
      }

      const result = await fetchHasConfiguredBrokers(clientId);

      if (owningClient.value?.id === clientId) {
        hasConfiguredBrokers.value = result;
      }
    },
    { immediate: true },
  );

  return hasConfiguredBrokers;
};
